import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Bell, Package, Users, AlertTriangle, ShoppingBag, Check, CheckCheck, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NOTIF_ICONS = {
  new_order: <ShoppingBag className="w-4 h-4 text-green-400" />,
  new_user: <Users className="w-4 h-4 text-blue-400" />,
  low_stock: <AlertTriangle className="w-4 h-4 text-yellow-400" />,
  out_of_stock: <Package className="w-4 h-4 text-red-400" />,
  new_message: <MessageCircle className="w-4 h-4 text-fuchsia-400" />,
};

const NOTIF_COLORS = {
  new_order: 'bg-green-500/10 border-green-500/20',
  new_user: 'bg-blue-500/10 border-blue-500/20',
  low_stock: 'bg-yellow-500/10 border-yellow-500/20',
  out_of_stock: 'bg-red-500/10 border-red-500/20',
  new_message: 'bg-fuchsia-500/10 border-fuchsia-500/20',
};

const formatTimeAgo = (dateStr) => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "à l'instant";
  if (mins < 60) return `il y a ${mins}min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `il y a ${hours}h`;
  const days = Math.floor(hours / 24);
  return `il y a ${days}j`;
};

export const NotificationBell = () => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/notifications/unread-count`, { withCredentials: true });
      setUnreadCount(res.data.count);
    } catch { /* ignore if not admin */ }
  }, []);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/notifications`, { withCredentials: true });
      setNotifications(res.data.notifications);
    } catch { /* ignore */ }
    setLoading(false);
  };

  const handleToggle = () => {
    if (!open) fetchNotifications();
    setOpen(!open);
  };

  const markAsRead = async (id) => {
    try {
      await axios.put(`${API}/admin/notifications/${id}/read`, {}, { withCredentials: true });
      setNotifications(prev => prev.map(n => n.notification_id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch { /* ignore */ }
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/admin/notifications/read-all`, {}, { withCredentials: true });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch { /* ignore */ }
  };

  return (
    <div className="relative" ref={ref} data-testid="notification-bell-container">
      <Button
        variant="ghost"
        size="icon"
        className="text-gray-300 hover:text-white hover:bg-white/10 relative"
        onClick={handleToggle}
        data-testid="notification-bell-button"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs flex items-center justify-center text-white font-medium animate-pulse" data-testid="notification-badge">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </Button>

      {open && (
        <div className="absolute right-0 top-12 w-80 sm:w-96 bg-[#121212] border border-white/10 rounded-xl shadow-2xl z-50 overflow-hidden" data-testid="notification-panel">
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            <h3 className="text-white font-semibold text-sm">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                className="text-xs text-fuchsia-400 hover:text-fuchsia-300 flex items-center gap-1"
                data-testid="mark-all-read-button"
              >
                <CheckCheck className="w-3 h-3" /> Tout marquer comme lu
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="w-6 h-6 border-2 border-fuchsia-600 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="text-center py-8">
                <Bell className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                <p className="text-gray-500 text-sm">Aucune notification</p>
              </div>
            ) : (
              notifications.map((notif) => (
                <div
                  key={notif.notification_id}
                  className={`flex items-start gap-3 p-3 border-b border-white/5 transition-colors cursor-pointer hover:bg-white/5 ${
                    !notif.read ? 'bg-white/[0.02]' : ''
                  }`}
                  onClick={() => !notif.read && markAsRead(notif.notification_id)}
                  data-testid={`notification-item-${notif.notification_id}`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 border ${NOTIF_COLORS[notif.type] || 'bg-gray-500/10 border-gray-500/20'}`}>
                    {NOTIF_ICONS[notif.type] || <Bell className="w-4 h-4 text-gray-400" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className={`text-sm font-medium truncate ${!notif.read ? 'text-white' : 'text-gray-400'}`}>
                        {notif.title}
                      </p>
                      {!notif.read && <span className="w-2 h-2 bg-fuchsia-500 rounded-full flex-shrink-0" />}
                    </div>
                    <p className="text-xs text-gray-500 truncate">{notif.message}</p>
                    <p className="text-xs text-gray-600 mt-1">{formatTimeAgo(notif.created_at)}</p>
                  </div>
                  {!notif.read && (
                    <button className="text-gray-500 hover:text-fuchsia-400 flex-shrink-0 mt-1" title="Marquer comme lu">
                      <Check className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};
